/**
 * 文件主要负责生成不同类型的链接，例如，web链接，app链接，voltron链接等
 */
import { Component } from 'vue';
import { logger } from '_c/util/logger';
import { CommonLinkTextNodeType, TextNode } from '_c/typings/model/chat';
import { CommonLinkTextNode, CommonLinkTextType } from '_c/typings/model/chat/text-node';

const TAG = 'generateCommonLinkTextNodeConfig';

interface CommonLinkTextNodeConfigProps {
  displayName: string,
  ext: Record<string, unknown>,
}

/**
 * 链接配置基础结构
 */
interface CommonLinkTextNodeConfigDefault {
  type: CommonLinkTextNodeType,
  // 组件内属性，默认为空对象
  props: CommonLinkTextNodeConfigProps,
  // 是否合法，默认为true
  isValid: boolean,
  // 是否是未知消息或者不合法消息，此类消息不响应事件处理
  isUnknown: boolean,
}

export interface CommonLinkTextNodeConfig extends CommonLinkTextNodeConfigDefault{
  // 展示组件，默认为未知组件
  component: Component,
}

interface CommonLinkTextNodeConfigOptional extends Partial<CommonLinkTextNodeConfig> {
  // 展示组件，默认为未知组件
  component: Component,
}

/**
 * 生成默认的消息配置
 * @param node
 */
const generateDefaultCommonLinkTextConfig = (node: CommonLinkTextNode): CommonLinkTextNodeConfigDefault => ({
  type: CommonLinkTextNodeType.unknown,
  props: {
    displayName: node.displayName,
    ext: node.ext,
  },
  isValid: true,
  isUnknown: false,
});


// export function extractCommonLinkTextNodeConfig(text: string): CommonLinkTextNodeConfig {
//   let commonLinkTextNodeConfig: CommonLinkTextNodeConfig;
//   let config: Record<string, unknown> = {};
//   let type = CommonLinkTextNodeType.unknown;
//   try {
//     config = JSON.parse(text);
//     type = config.type as CommonLinkTextNodeType;
//   } catch (err) {
//     logger.error(TAG, 'JSON.parse fail, err: ', err);
//   }
//   return CommonLinkTextConfig;
// }

const commonLinkTextNodeGenerateFuncMap:
Partial<Record<CommonLinkTextNodeType, (node: CommonLinkTextNode) => CommonLinkTextNodeConfig>> = {};

let generateInvalidCommonLinkTextNodeConfig: (node?: CommonLinkTextNode) => CommonLinkTextNodeConfig;
let generateUnknownCommonLinkTextNodeConfig: (node: CommonLinkTextNode) => CommonLinkTextNodeConfig;

const setInvalidCommonLinkTextNodeConfig = (options: CommonLinkTextNodeConfigOptional
| ((node?: CommonLinkTextNode) => CommonLinkTextNodeConfig)): void => {
  generateInvalidCommonLinkTextNodeConfig = (node?: CommonLinkTextNode) => {
    let opts: CommonLinkTextNodeConfigOptional;
    if (typeof options === 'function') {
      opts = options(node);
    } else {
      opts = { ...options };
    }
    return {
      type: CommonLinkTextNodeType.unknown,
      // 组件内属性，默认为空对象
      props: {
        displayName: '不支持的文字类型',
        ext: {},
      },
      // 是否合法，默认为true
      isValid: true,
      // 是否是未知消息或者不合法消息，此类消息不响应事件处理
      isUnknown: false,
      ...opts,
    };
  };
};

const setUnknownCommonLinkTextNodeConfig = (options: CommonLinkTextNodeConfigOptional
| ((node: CommonLinkTextNode) => CommonLinkTextNodeConfigOptional)): void => {
  generateUnknownCommonLinkTextNodeConfig = (node: CommonLinkTextNode) => {
    let opts: CommonLinkTextNodeConfigOptional;
    if (typeof options === 'function') {
      opts = options(node);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultCommonLinkTextConfig(node),
      ...opts,
    };
  };
};

const setCommonLinkTextNodeConfigGenerateFunc = (
  commonLinkTextNodeType: CommonLinkTextNodeType,
  options: CommonLinkTextNodeConfigOptional | ((node: CommonLinkTextNode) => CommonLinkTextNodeConfigOptional),
): void => {
  if (CommonLinkTextNodeType[commonLinkTextNodeType]) {
    commonLinkTextNodeGenerateFuncMap[commonLinkTextNodeType] = (commonLinkTextNode: CommonLinkTextNode) => {
      let opts: CommonLinkTextNodeConfigOptional;
      if (typeof options === 'function') {
        opts = options(commonLinkTextNode);
      } else {
        opts = { ...options };
      }
      return {
        ...generateDefaultCommonLinkTextConfig(commonLinkTextNode),
        type: commonLinkTextNodeType,
        ...opts,
      };
    };
  }
};

const extractCommonLinkTextNodeConfig = (textNode: TextNode): CommonLinkTextNodeConfig => {
  let commonLinkTextNode: CommonLinkTextNode | undefined = undefined;
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(textNode.innerText);
    commonLinkTextNode = {
      type: config.type as CommonLinkTextType,
      displayName: config.displayName as string,
      ext: config.ext as Record<string, unknown>,
    };
  } catch (err) {
    logger.error(TAG, 'JSON.parse fail, err: ', err);
  }
  if (!commonLinkTextNode) {
    return generateInvalidCommonLinkTextNodeConfig();
  }
  const fn = commonLinkTextNodeGenerateFuncMap[commonLinkTextNode.type];
  if (fn) {
    try {
      const commonLinkTextNodeConfig = fn.call(undefined, commonLinkTextNode);
      if (commonLinkTextNodeConfig.isValid) {
        return commonLinkTextNodeConfig;
      }
      return generateInvalidCommonLinkTextNodeConfig(commonLinkTextNode);
    } catch (err) {
      logger.error(TAG, 'generate common link text node config fail, err', err);
    }
  }
  if (typeof generateUnknownCommonLinkTextNodeConfig === 'function') {
    return generateUnknownCommonLinkTextNodeConfig(commonLinkTextNode);
  }
  logger.error(TAG, 'generate unknown config fail');
  throw `${TAG}, generate unknown config fail`;
};

export {
  setUnknownCommonLinkTextNodeConfig,
  setInvalidCommonLinkTextNodeConfig,
  setCommonLinkTextNodeConfigGenerateFunc,
  extractCommonLinkTextNodeConfig,
};
